import React from 'react'
// libs
import { Field, WrappedFieldProps } from 'redux-form'
import { Checkbox } from '@mui/material'
// components
import InputLabel from 'components/FormElements/components/InputLabel'
import ErrorMessage from 'components/UI/ErrorMessage'
// constants
import { API_SCOPE_ACTIONS, API_SCOPE_ENTITIES, ApiScopeAction } from 'constants/apiClient'
// types
import { Validators } from 'utils/validators'
// utils
import { buildScope, parseScope } from 'utils/apiClient'
// views
import { Grid, Hint, Wrapper } from './views'

interface Props {
  name: string
  validate?: Validators
}

const renderComponent = ({ input, meta }: Props & WrappedFieldProps) => {
  const selected: string[] = Array.isArray(input.value) ? input.value : []

  const has = (scope: string) => selected.includes(scope)

  const toggle = (entity: string, action: ApiScopeAction) => () => {
    const scope = buildScope(entity, action)
    const set = new Set(selected)

    if (set.has(scope)) {
      set.delete(scope)
      // Removing `read` removes every other action for the same entity.
      if (action === 'read') {
        API_SCOPE_ACTIONS.forEach((a) => set.delete(buildScope(entity, a)))
      }
    } else {
      set.add(scope)
      // Any non-read action implies `read` on the same entity.
      if (action !== 'read') {
        set.add(buildScope(entity, 'read'))
      }
    }

    input.onChange(
      [...set].sort((a, b) => {
        const pa = parseScope(a)
        const pb = parseScope(b)
        return pa.entity === pb.entity
          ? API_SCOPE_ACTIONS.indexOf(pa.action as ApiScopeAction) -
              API_SCOPE_ACTIONS.indexOf(pb.action as ApiScopeAction)
          : a.localeCompare(b)
      })
    )
  }

  const { touched, error } = meta

  return (
    <Wrapper>
      <InputLabel shrink label='Scopes' required />
      <Grid>
        <thead>
          <tr>
            <th/>
            {API_SCOPE_ACTIONS.map((action) => (
              <th key={action}>{action}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {API_SCOPE_ENTITIES.map((entity) => (
            <tr key={entity.key}>
              <td>{entity.label}</td>
              {API_SCOPE_ACTIONS.map((action) => (
                <td key={action}>
                  <Checkbox
                    size='small'
                    checked={has(buildScope(entity.key, action))}
                    onChange={toggle(entity.key, action)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Grid>
      <Hint>Every non-read action requires the matching “read” scope.</Hint>
      {touched && error && <ErrorMessage>{error}</ErrorMessage>}
    </Wrapper>
  )
}

const ScopesField: React.FC<Props> = ({ name, ...rest }) => <Field {...rest} name={name} component={renderComponent} />

export default ScopesField
